import { useFlag } from '@reflag/react-sdk';

import type * as api from '@gitbook/api-client';
import { createPalette, PaletteButton, PaletteCommandType } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { useNavigation } from '../../../navigation';
import { SidebarActionsButton } from '../../../Sidebars/SidebarActionsButton';
import { apiClient, type SiteCapability } from '../../../state';
import { useSiteAudienceCommand } from '../../AdaptiveContent';
import { useDuplicateSpaceCommand } from '../../Space';
import { getPaletteLocationOptions } from './getPaletteLocationOptions';
import { getPaletteSpaceOptions } from './getPaletteSpaceOptions';
import { useDeleteSiteItems } from './useDeleteSiteItems';
import { useSetDefaultSiteVariant } from './useSetDefaultSiteVariant';
import { useSetSiteSpaceVisibility } from './useSetSiteSpaceVisibility';
import { useSiteContentDraftCommand } from './useSiteContentDraft';

export interface SiteVariantActionsButtonProps extends Pick<ButtonProps, 'size' | 'disabled'> {
    /**
     * Controls whether the trigger should match sidebar item actions.
     */
    kind?: 'default' | 'sidebar';
    siteSpace: api.SiteSpace;
    site: api.Site;
    organization: api.Organization;
    sitePermissions: SiteCapability;
    siteStructure: api.SiteStructure;
}

/**
 * Action button to open actions palette for a site variant.
 */
export function SiteVariantActionsButton(props: SiteVariantActionsButtonProps) {
    const {
        siteSpace,
        site,
        organization,
        sitePermissions,
        siteStructure,
        kind = 'default',
        ...buttonProps
    } = props;
    const organizationId = organization.id;
    const hideSiteSpaceFeature = useFlag('HIDDEN_SITES_SPACE');
    const deleteSiteItems = useDeleteSiteItems({ organization, site });
    const setDefaultVariant = useSetDefaultSiteVariant({ organization, site, siteStructure });
    const setSiteVariantVisibility = useSetSiteSpaceVisibility({
        organization,
        site,
        type: 'variant',
    });

    const { navigate } = useNavigation();
    const siteAudience = useSiteAudienceCommand({
        organizationId,
        site,
        target: 'variant',
        condition: siteSpace.condition ? siteSpace.condition : undefined,
        onChange: async (condition) => {
            await apiClient.siteSpaces.updateSiteSpaceById(
                organizationId,
                site.id,
                siteSpace.id,
                {
                    condition: condition ?? null,
                },
                { returnPreference: 'minimal' }
            );
        },
    });

    const draftCommand = useSiteContentDraftCommand({
        organization,
        site,
        siteStructure,
        content: siteSpace,
        canCreateSite: sitePermissions.canCreateSite,
    });
    const duplicateSpace = useDuplicateSpaceCommand(siteSpace.space, {
        siteId: site.id,
        sitePermissions: site.permissions,
        siteSpaceId: siteSpace.id,
        siteItemType: 'variant',
    });

    const palette = () => {
        return createPalette({
            groups: [
                kind === 'sidebar'
                    ? {
                          key: 'edit',
                          commands: [
                              {
                                  key: 'siteSpace.edit',
                                  title: 'Edit navigation item',
                                  icon: Icon.Edit,
                                  disabled: !sitePermissions.canEditSite,
                                  subtitle: sitePermissions.canEditSite
                                      ? undefined
                                      : 'You need permission to edit this site navigation.',
                                  trigger: () => {
                                      navigate({
                                          screen: {
                                              type: 'site',
                                              organization: organization.id,
                                              site: site.id,
                                              tab: {
                                                  type: 'structure',
                                                  selected: siteSpace.id,
                                              },
                                          },
                                      });
                                  },
                              },
                          ],
                      }
                    : null,
                siteSpace.urls.published
                    ? getPaletteLocationOptions(siteSpace.urls.published, {
                          openTitle: 'Open in site',
                      })
                    : null,
                getPaletteSpaceOptions({
                    space: siteSpace.space,
                    siteId: site.id,
                    navigate,
                }),
                kind === 'sidebar'
                    ? {
                          key: 'duplicate',
                          commands: [duplicateSpace.command()].filter(
                              (command) => command !== null
                          ),
                      }
                    : null,
                sitePermissions.canCreateSite
                    ? {
                          key: 'update-options',
                          commands: [
                              {
                                  key: 'siteSpace.setDefault',
                                  title: 'Set as home',
                                  icon: Icon.Home,
                                  disabled: Boolean(siteSpace.default) || Boolean(siteSpace.hidden),
                                  subtitle: siteSpace.hidden
                                      ? 'Hidden from site navigation'
                                      : siteSpace.default
                                        ? 'Already set as home'
                                        : undefined,
                                  trigger: () => {
                                      setDefaultVariant(siteSpace);
                                  },
                              },
                              draftCommand,
                              hideSiteSpaceFeature.isEnabled
                                  ? {
                                        key: 'siteSpace.hide',
                                        enabled: !siteSpace.hidden,
                                        type: PaletteCommandType.SWITCH as const,
                                        title: 'Show in site navigation',
                                        subtitle: siteSpace.default
                                            ? "Home variant can't be hidden"
                                            : undefined,
                                        icon: Icon.Eye,
                                        disabled: Boolean(siteSpace.default),
                                        trigger: () => {
                                            setSiteVariantVisibility(siteSpace);
                                        },
                                    }
                                  : null,
                              siteAudience?.command ?? null,
                              {
                                  key: 'siteSpace.delete',
                                  title: 'Remove',
                                  icon: Icon.Trashcan,
                                  kind: 'danger' as const,
                                  trigger: () => {
                                      deleteSiteItems([siteSpace]);
                                  },
                              },
                          ].filter((x) => x !== null),
                      }
                    : {
                          key: 'draft-options',
                          commands: [draftCommand],
                      },
            ].filter((x) => x !== null),
        });
    };

    return (
        <>
            {siteAudience?.dialogs}
            {duplicateSpace.dialog}
            {(() => {
                switch (kind) {
                    case 'sidebar':
                        return (
                            <SidebarActionsButton
                                palette={palette}
                                disabled={buttonProps.disabled}
                            />
                        );
                    case 'default':
                        return (
                            <PaletteButton
                                palette={palette}
                                kind="blank"
                                label="Actions"
                                leadingIcon={Icon.MoreVertical}
                                iconOnly
                                {...buttonProps}
                            />
                        );
                    default:
                        return assertNever(kind);
                }
            })()}
        </>
    );
}
